import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import LiveBalance from '../../components/LiveBalance';
import GameCard from './GameCard';
import CashoutRulesPopup from './CashoutRulesPopup';
import ExistingBalanceWarning from './ExistingBalanceWarning';
import { Gamepad2, Star, Copy, ArrowUpCircle, RefreshCw, AlertTriangle, ScrollText, ChevronLeft, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { gameImageUrl, handleImageFallback } from '../../lib/utils';
import { trackEvent } from '../../lib/analytics';

// Iter 142.10: any game balance above this threshold blocks a load until
// the player redeems. $0.20 is small enough to ignore "rounding cents"
// but big enough to catch genuine forgotten balances.
const LOAD_BLOCK_BALANCE_THRESHOLD = 0.20;

export default function GamesTab({ games, accounts, refresh, initialGameId, activeTab, wallet = {}, onDeposit, onOptimisticAccount }) {
  const [loadAmounts, setLoadAmounts] = useState({});
  // Fund source per-account: "wallet" ($) or "fp" (Free Play credits).
  const [fundSource, setFundSource] = useState({});
  const [processing, setProcessing] = useState({});
  const [cardMessages, setCardMessages] = useState({});
  const [showLoadModal, setShowLoadModal] = useState(null);
  // Iter 142.10: state for new player popups
  const [showCashoutRules, setShowCashoutRules] = useState(null); // game obj or null
  const [existingBalanceGate, setExistingBalanceGate] = useState(null); // { acc, sg, balance } or null
  const [insufficientLoadModal, setInsufficientLoadModal] = useState(null);
  const [selectedGame, setSelectedGame] = useState(initialGameId || null);
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem('vl_favorites') || '[]'); } catch { return []; }
  });
  const [gameFilter, setGameFilter] = useState('all');

  useEffect(() => {
    if (initialGameId) {
      setSelectedGame(initialGameId);
      window.__expandGameId = null;
    }
  }, [initialGameId]);

  // Collapse the expanded game card whenever the user leaves the games tab.
  useEffect(() => {
    if (activeTab && activeTab !== 'games' && selectedGame) {
      setSelectedGame(null);
    }
  }, [activeTab, selectedGame]);

  const toggleFav = (gameId) => {
    setFavorites(prev => {
      const next = prev.includes(gameId) ? prev.filter(id => id !== gameId) : [...prev, gameId];
      localStorage.setItem('vl_favorites', JSON.stringify(next));
      return next;
    });
  };

  const setMsg = (accId, msg, type = 'info') => {
    setCardMessages(prev => ({ ...prev, [accId]: { msg, type, ts: Date.now() } }));
    setTimeout(() => setCardMessages(prev => {
      if (prev[accId]?.ts <= Date.now() - 4500) return { ...prev, [accId]: null };
      return prev;
    }), 5000);
  };
  const setProc = (key, val) => setProcessing(prev => ({ ...prev, [key]: val }));

  const handleCreateAccount = async (gameId) => {
    setProc(`create_${gameId}`, true);
    try {
      const res = await api.post('/api/games/create-account', { game_id: gameId });
      // iter 131: OPTIMISTIC UPDATE — the create response IS the new
      // account object (backend returns it directly, not wrapped). Push
      // it into local state so the UI reflects the new account in <100ms;
      // refresh() then reconciles with the authoritative server list.
      // Previously we relied solely on refresh() + a service-worker-cached
      // list, which delayed the render by ~30s (one SW cache lifetime).
      if (res?.data && (res.data.id || res.data.game_id) && onOptimisticAccount) {
        onOptimisticAccount(res.data);
      }
      await refresh();
      // Iter 137: celebratory burst when an account is created — replaces
      // the simple toast. Falls back to toast if the burst trigger
      // isn't mounted yet (e.g. early route).
      const game = (games || []).find(g => g.id === gameId);
      trackEvent('game_account_created', {
        game_id: gameId,
        game_name: game?.name,
        provider_code: game?.provider_code || game?.code,
      });
      if (typeof window !== 'undefined' && window.__vlTriggerBurst) {
        window.__vlTriggerBurst({
          title: 'Account ready!',
          subtitle: game ? `Logged in to ${game.name}` : 'Credentials are ready',
          color: '#22c55e',
          duration: 1700,
        });
      } else {
        toast.success('Game account created! Your credentials are ready.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Failed');
    } finally {
      setProc(`create_${gameId}`, false);
    }
  };

  const handleCheckBalance = async (accId) => {
    setProc(`bal_${accId}`, true);
    try {
      const res = await api.post(`/api/games/accounts/${accId}/check-balance`);
      const d = res.data; const balUsd = d.balance_usd || 0;
      trackEvent('game_balance_checked', {
        account_id: accId,
        balance: Number(balUsd || 0),
        cached: Boolean(d.is_cached),
      });
      setMsg(accId, `$${balUsd.toFixed ? balUsd.toFixed(2) : balUsd} ${d.is_cached ? 'cached' : 'LIVE'}`, d.is_cached ? 'warn' : 'success');
      refresh();
    } catch (err) { setMsg(accId, err.response?.data?.message || 'Failed', 'error'); }
    finally { setProc(`bal_${accId}`, false); }
  };

  const handleLoad = async (accId, gameId) => {
    const amount = loadAmounts[accId];
    if (!amount || parseFloat(amount) <= 0) { setMsg(accId, 'Enter valid amount', 'error'); return; }
    const source = fundSource[accId] || 'wallet';
    setProc(`load_${accId}`, true);
    try {
      const res = await api.post('/api/games/load', {
        game_id: gameId,
        account_id: accId,
        amount: parseFloat(amount),
        fund_source: source,
      });
      const label = source === 'fp' ? `FP: -${amount}` : `Wallet: -$${amount}`;
      trackEvent('game_load_completed', {
        game_id: gameId,
        account_id: accId,
        amount: parseFloat(amount),
        fund_source: source,
      });
      setMsg(accId, `Loaded $${res.data.provider_new_balance ?? '?'} · ${label}`, 'success');
      setLoadAmounts(prev => ({ ...prev, [accId]: '' })); setShowLoadModal(null); refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = detail?.message || err.response?.data?.message || (typeof detail === 'string' ? detail : null) || 'Load failed. Please try again.';
      if (/insufficient|not enough|balance/i.test(message)) {
        setInsufficientLoadModal({ accId, gameId, amount: parseFloat(amount), source, message });
      } else {
        setMsg(accId, message, 'error');
      }
    }
    finally { setProc(`load_${accId}`, false); }
  };

  // Iter 142.10: Pre-load balance gate.
  // Tapping "Load" on the game card triggers this first. We fetch the
  // live provider balance and, if it's above the threshold, ask the
  // player to redeem before loading. Falls through to opening the load
  // modal when the balance is clean.
  const handleLoadClick = async (acc, sg) => {
    setProc(`gate_${acc.id}`, true);
    try {
      const res = await api.post(`/api/games/accounts/${acc.id}/check-balance`);
      const live = Number(res.data?.balance_usd ?? res.data?.balance ?? 0);
      if (live > LOAD_BLOCK_BALANCE_THRESHOLD) {
        // Block — show the redeem-first popup. Stash enough context so
        // the popup's redeem button can call back to handleRedeem.
        setExistingBalanceGate({ acc, sg, balance: live });
        return;
      }
      // Clean — proceed to the existing load modal.
      setShowLoadModal((prev) => (prev === acc.id ? null : acc.id));
    } catch (err) {
      // If balance check itself fails, fall through to the load modal —
      // we don't want a flaky upstream to lock the player out of loading.
      // Surface a small inline note so they know.
      setMsg(acc.id, 'Could not verify game balance — proceed with caution.', 'warn');
      setShowLoadModal((prev) => (prev === acc.id ? null : acc.id));
    } finally {
      setProc(`gate_${acc.id}`, false);
    }
  };

  // Iter 142.10: redeem-from-popup. Reuses the regular handleRedeem
  // logic but, on success, transitions the player into the load modal
  // so they don't have to re-tap.
  const handleGateRedeem = async () => {
    const gate = existingBalanceGate;
    if (!gate) return;
    setProc(`gate_redeem_${gate.acc.id}`, true);
    try {
      await handleRedeem(gate.acc.id, gate.sg.id);
      // Close gate, open load modal.
      setExistingBalanceGate(null);
      setShowLoadModal(gate.acc.id);
    } finally {
      setProc(`gate_redeem_${gate.acc.id}`, false);
    }
  };

  const handleRedeem = async (accId, gameId) => {
    setProc(`redeem_${accId}`, true);
    try {
      // Iter 142.3: One-click withdraw — no amount input. Server pulls
      // the live provider balance and applies provider rules (min/max
      // multiplier, fee) automatically. Funds are routed back to the
      // SAME bucket the player loaded with: FP → FP, cash → cash.
      const res = await api.post(
        '/api/games/redeem',
        { game_id: gameId, account_id: accId },
        { headers: { 'X-Idempotency-Key': `redeem:${accId}:${Date.now()}` } }
      );
      const d = res.data || {};
      const credited = Number(d.wallet_credited ?? d.net_amount ?? 0);
      trackEvent('game_redeem_completed', {
        game_id: gameId,
        account_id: accId,
        amount: credited,
        credited_to: d.credited_to || 'wallet',
      });
      const bucket = d.credited_to === 'fp' ? 'FP' : 'wallet';
      const symbol = d.credited_to === 'fp' ? '' : '$';
      const suffix = d.credited_to === 'fp' ? ' FP' : '';
      const voided = Number(d.voided_amount || 0);
      let msg;
      if (credited <= 0 && voided > 0) {
        msg = `Redeemed — $${voided.toFixed(2)} voided per provider rules`;
      } else if (credited <= 0) {
        msg = 'Redeemed — provider rules paid out $0 (multiplier not yet met)';
      } else if (voided > 0) {
        msg = `Withdrew ${symbol}${credited.toFixed(2)}${suffix} to your ${bucket} (over-cap $${voided.toFixed(2)} voided)`;
      } else {
        msg = `Withdrew ${symbol}${credited.toFixed(2)}${suffix} to your ${bucket}`;
      }
      setMsg(accId, msg, 'success');
      refresh();
    } catch (err) {
      setMsg(accId, err.response?.data?.message || err.response?.data?.detail || 'Failed', 'error');
    } finally {
      setProc(`redeem_${accId}`, false);
    }
  };

  const copyText = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };
  const scrollFieldIntoView = (event) => {
    const el = event.currentTarget;
    window.setTimeout(() => {
      el?.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    }, 180);
  };

  const allGames = games || [];
  const accountList = accounts || [];
  const hasAccountForGame = (game) => accountList.some((a) => (
    a.game_id === game.id || a.game_code === game.code || a.game_code === game.provider_code
  ));
  const filterTabs = [
    { id: 'all', label: 'All Games', count: allGames.length },
    { id: 'mine', label: 'My Games', count: allGames.filter(hasAccountForGame).length },
    { id: 'favorites', label: 'Favourites', count: favorites.length },
  ];
  const displayGames = allGames.filter((game) => {
    if (gameFilter === 'mine') return hasAccountForGame(game);
    if (gameFilter === 'favorites') return favorites.includes(game.id);
    return true;
  });

  const glowStyle = `
    @keyframes btnGlow{0%,100%{box-shadow:0 0 8px rgba(108,92,231,0.4)}50%{box-shadow:0 0 18px rgba(108,92,231,0.6)}}
    @keyframes btnGlowG{0%,100%{box-shadow:0 0 8px rgba(34,197,94,0.4)}50%{box-shadow:0 0 18px rgba(34,197,94,0.6)}}
    @keyframes btnGlowD{0%,100%{box-shadow:0 0 8px rgba(212,175,55,0.3)}50%{box-shadow:0 0 16px rgba(212,175,55,0.5)}}
    .glow-p{animation:btnGlow 2s ease-in-out infinite}.glow-g{animation:btnGlowG 2s ease-in-out infinite}.glow-d{animation:btnGlowD 2s ease-in-out infinite}
  `;

  // Get selected game data
  const sg = selectedGame ? allGames.find(g => g.id === selectedGame) : null;
  const sgImg = sg ? gameImageUrl(sg, '') : '';
  const sgAccs = sg ? accountList.filter(a => a.game_id === sg.id || a.game_code === sg.code || a.game_code === sg.provider_code) : [];

  return (
    <div className="vl-pirate-page vl-games-page space-y-3 pb-28 anim-fadeInUp" data-testid="games-tab">
      <style>{glowStyle}</style>

      <div className="space-y-2.5 -mt-1">
        <div className="grid grid-cols-3 gap-2 rounded-2xl p-1.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="games-filter-tabs">
          {filterTabs.map((tab) => (
            <button
              type="button"
              key={tab.id}
              onClick={() => setGameFilter(tab.id)}
              className={`rounded-xl py-2.5 px-2 text-sm font-black transition ${gameFilter === tab.id ? 'text-black' : 'text-gray-300 hover:text-white'}`}
              style={{ background: gameFilter === tab.id ? 'linear-gradient(135deg,#D4AF37,#F5D76E)' : 'transparent' }}
              data-testid={`games-filter-${tab.id}`}
            >
              {tab.label}
              <span className={`ml-1 text-[10px] ${gameFilter === tab.id ? 'text-black/70' : 'text-gray-500'}`}>{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Games Grid — Iter 133: staggered reveal + premium card lift.
          Iter 141: GameCard extracted into its own memo'd component. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 anim-stagger-fast">
        {displayGames.map((game) => (
          <GameCard
            key={game.id}
            game={game}
            favorites={favorites}
            onSelect={(id) => {
              const selected = allGames.find(g => g.id === id);
              trackEvent('game_opened', {
                game_id: id,
                game_name: selected?.name,
                provider_code: selected?.provider_code || selected?.code,
                has_account: accountList.some(a => a.game_id === id || a.game_code === selected?.code || a.game_code === selected?.provider_code),
              });
              setSelectedGame(id);
              window.__expandGameId = null;
            }}
            onToggleFav={toggleFav}
          />
        ))}
        {displayGames.length === 0 && <p className="col-span-full text-center text-gray-500 py-8">No games in this view</p>}
      </div>

      {/* ========== GAME DETAIL POPUP — portal into main popup host, absolute inset-0 ========== */}
      {sg && (() => {
        const host = typeof document !== 'undefined' ? document.getElementById('player-popup-host') : null;
        const popup = (
          <div
            className="absolute inset-0 z-40 flex flex-col pointer-events-auto"
            style={{ background: 'linear-gradient(180deg, #0f0a1e 0%, #0a0e17 100%)' }}
            data-testid="game-detail-popup"
          >
            {/* Close Header */}
            <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <button onClick={() => setSelectedGame(null)} className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-white/10 active:scale-95 transition-all flex-shrink-0" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }} aria-label="Back">
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <h2 className="text-lg font-bold text-white truncate flex-1 min-w-0">{sg.name}</h2>
            </div>

            {/* Scrollable Content */}
            <div
              className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
              style={{
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
                scrollPaddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))',
              }}
            >
              {/* Large Game Image */}
              <div className="w-full aspect-[16/9] rounded-2xl overflow-hidden relative" style={{ boxShadow: '0 4px 30px rgba(0,0,0,0.5)' }}>
                {sgImg ? <img src={sgImg} alt={sg.name} loading="lazy" decoding="async" onError={handleImageFallback} className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center text-6xl font-bold bg-gradient-to-br from-[#1e1245] to-[#0d1117]" style={{ color: '#D4AF37' }}>{sg.name?.charAt(0)}</div>}
                <div className="absolute bottom-0 left-0 right-0 p-4" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.9))' }}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-xl text-white truncate flex-1 min-w-0">{sg.name}</h3>
                    <button onClick={() => toggleFav(sg.id)} className="p-2 rounded-full hover:bg-white/10 flex-shrink-0">
                      <Star className={`w-6 h-6 ${favorites.includes(sg.id) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-400'}`} />
                    </button>
                  </div>
                  {sgAccs.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <p className="font-bold text-2xl flex items-center gap-2">
                        <LiveBalance
                          value={Number(sgAccs[0]?.last_known_balance) || 0}
                          prefix="$"
                          decimals={2}
                          syncedAt={sgAccs[0]?.last_balance_synced_at}
                          showLiveDot={false}
                          className="text-2xl text-[#F5D76E]"
                          data-testid="game-detail-balance-usd"
                        />
                      </p>
                      <button
                        onClick={() => handleCheckBalance(sgAccs[0].id)}
                        disabled={processing[`bal_${sgAccs[0].id}`]}
                        className="p-1 rounded-lg hover:bg-emerald-500/10 transition leading-none disabled:opacity-50"
                        data-testid={`game-popup-refresh-balance-${sgAccs[0].id}`}
                        aria-label="Refresh balance"
                        title="Refresh balance from provider"
                      >
                        {processing[`bal_${sgAccs[0].id}`]
                          ? <div className="spinner w-5 h-5" />
                          : <RefreshCw className="w-5 h-5 text-emerald-400" />}
                      </button>
                    </div>
                  )}
                </div>
                {sg.hot && (
                  <span className="vl-game-card__hot absolute top-3 right-3" aria-label="Hot game">
                    <img src="/assets/fire-svgrepo-com.svg" alt="" className="vl-game-card__fire-svg" draggable="false" />
                  </span>
                )}
              </div>

              {/* Account Details */}
              {sgAccs.length === 0 ? (
                sg.provider_status === 'coming_soon' ? (
                  <div className="w-full rounded-2xl p-4 text-center" style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)' }}>
                    <AlertTriangle className="w-7 h-7 text-blue-400 mx-auto mb-2" />
                    <p className="text-blue-300 font-semibold text-sm">Coming Soon</p>
                    <p className="text-gray-400 text-xs mt-1">This game will be available for automatic account creation soon.</p>
                  </div>
                ) : sg.provider_status === 'manual_only' ? (
                  <div className="w-full rounded-2xl p-4 text-center" style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)' }}>
                    <AlertTriangle className="w-7 h-7 text-orange-400 mx-auto mb-2" />
                    <p className="text-orange-300 font-semibold text-sm">Under Maintenance</p>
                    <p className="text-gray-400 text-xs mt-1">This game is temporarily unavailable. Contact support for assistance.</p>
                  </div>
                ) : (
                  <Button onClick={() => handleCreateAccount(sg.id)} disabled={processing[`create_${sg.id}`]}
                    className="w-full h-14 rounded-2xl font-bold text-base glow-d" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D76E)', color: '#000' }} data-testid={`create-account-${sg.id}`}>
                    {processing[`create_${sg.id}`] ? (
                      <span className="flex items-center justify-center gap-3">
                        <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-black/10">
                          <span className="absolute inset-0 rounded-full border-2 border-black/15 border-t-black animate-spin" />
                          <Sparkles className="w-3.5 h-3.5 text-black" />
                        </span>
                        <span className="flex flex-col items-start leading-tight">
                          <span>Creating your account</span>
                          <span className="text-[11px] font-semibold text-black/60 tracking-wide">Setting up secure login...</span>
                        </span>
                      </span>
                    ) : '+ Create Game Account'}
                  </Button>
                )
              ) : sgAccs.map(acc => (
                <div key={acc.id} className="space-y-4">
                  {/* Credentials Boxes */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl p-4 flex items-center justify-between" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] text-gray-500 mb-1">Username</p>
                        <p className="text-base font-mono text-white truncate" data-testid={`username-value-${acc.id}`}>{acc.username || acc.provider_username}</p>
                      </div>
                      <button onClick={() => copyText(acc.username || acc.provider_username)} className="p-2 rounded-lg hover:bg-white/10 flex-shrink-0 ml-2" data-testid={`copy-user-${acc.id}`}>
                        <Copy className="w-5 h-5 text-[#D4AF37]" />
                      </button>
                    </div>
                    <div className="rounded-xl p-4 flex items-center justify-between" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] text-gray-500 mb-1">Password</p>
                        {/* Iter 142.13: password is now always shown in plain
                            text per user request — no masking. */}
                        <p className="text-base font-mono text-white truncate" data-testid={`password-value-${acc.id}`}>{acc.password || '—'}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0 ml-2">
                        {/* Iter 142.14: Reset-password button relocated.
                            Refresh-balance moved to the popup header
                            (next to the balance amount). The password
                            card now exposes only Copy — keeping it
                            visually quiet and matching the username card. */}
                        <button onClick={() => copyText(acc.password || '')} className="p-2 rounded-lg hover:bg-white/10" data-testid={`copy-pass-${acc.id}`}>
                          <Copy className="w-5 h-5 text-[#D4AF37]" />
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Iter 142.13: removed provider-specific password rule
                      hint pill — players don't need rule guidance now that
                      passwords are auto-generated and shown in plain text. */}

                  {/* Status Message */}
                  {cardMessages[acc.id] && (
                    <div className={`rounded-xl px-4 py-3 text-sm font-medium ${cardMessages[acc.id].type === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>{cardMessages[acc.id].msg}</div>
                  )}

                  {/* Action Buttons — Iter 142.4: removed standalone "Balance"
                      button per user request; balance refresh moved to a
                      compact circle-icon control on the password card. */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleLoadClick(acc, sg)}
                      disabled={processing[`gate_${acc.id}`]}
                      className="flex items-center justify-center gap-2 py-4 rounded-2xl text-base font-bold glow-p transition-all hover:scale-[1.03] disabled:opacity-60"
                      style={{ background: 'linear-gradient(135deg, #6C5CE7, #a29bfe)', color: '#fff', border: '2px solid rgba(108,92,231,0.5)' }}
                      data-testid={`add-credits-${acc.id}`}
                    >
                      {processing[`gate_${acc.id}`]
                        ? <div className="spinner w-5 h-5" />
                        : <>Load</>}
                    </button>
                    <button
                      onClick={() => handleRedeem(acc.id, sg.id)}
                      disabled={processing[`redeem_${acc.id}`]}
                      className="flex items-center justify-center gap-2 py-4 rounded-2xl text-base font-bold glow-g transition-all hover:scale-[1.03] disabled:opacity-60"
                      style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: '2px solid rgba(34,197,94,0.5)' }}
                      data-testid={`withdraw-credits-${acc.id}`}
                    >
                      {processing[`redeem_${acc.id}`]
                        ? <div className="spinner w-5 h-5" />
                        : <><ArrowUpCircle className="w-5 h-5" /> Redeem</>}
                    </button>
                  </div>

                  {/* Load Modal */}
                  {showLoadModal === acc.id && (
                    <div className="rounded-2xl p-4 space-y-3" style={{ background: 'rgba(108,92,231,0.05)', border: '1px solid rgba(108,92,231,0.2)' }}>
                      <p className="text-sm text-gray-300 font-medium">Load {sg.name}</p>
                      {/* Fund source toggle — Wallet ($) vs Free Play Credits.
                          Shows the player's balance on each tab so they can
                          see how much they can actually load from each. */}
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { id: 'wallet', label: 'Wallet $', bal: Number(wallet?.balance ?? wallet?.cash_balance ?? 0) },
                          { id: 'fp', label: 'FP Credits', bal: Number(wallet?.fp_credits ?? wallet?.play_credits ?? 0) },
                        ].map(src => {
                          const active = (fundSource[acc.id] || 'wallet') === src.id;
                          return (
                            <button
                              key={src.id}
                              onClick={() => setFundSource(p => ({ ...p, [acc.id]: src.id }))}
                              data-testid={`load-source-${src.id}-${acc.id}`}
                              className={`py-2 px-2 rounded-xl text-xs font-bold border transition text-left ${
                                active
                                  ? 'border-[#6C5CE7] bg-[#6C5CE7]/15 text-white'
                                  : 'border-white/10 text-gray-400 hover:text-gray-200'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span>{src.label}</span>
                                {active && <span className="w-1.5 h-1.5 rounded-full bg-[#A28BFE]" />}
                              </div>
                              <div className="text-[10px] text-gray-500 mt-0.5 tabular-nums">
                                {src.id === 'wallet' ? '$' : ''}{src.bal.toLocaleString(undefined, { maximumFractionDigits: 2 })}{src.id === 'fp' ? ' FP' : ''}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-3 gap-2">{[5, 10, 20, 30, 50, 100].map(v => (
                        <button key={v} onClick={() => setLoadAmounts(p => ({ ...p, [acc.id]: v.toString() }))}
                          className={`py-3.5 rounded-2xl text-base font-bold border transition ${loadAmounts[acc.id] === v.toString() ? 'bg-[#6C5CE7]/20 border-[#6C5CE7] text-[#A28BFE]' : 'border-white/10 text-gray-300 bg-black/10'}`}>{v}</button>
                      ))}</div>
                      <div className="flex gap-2">
                        <Input type="number" value={loadAmounts[acc.id] || ''} onFocus={scrollFieldIntoView} onChange={(e) => setLoadAmounts(p => ({ ...p, [acc.id]: e.target.value }))} placeholder={(fundSource[acc.id] || 'wallet') === 'fp' ? 'FP amount' : '$ amount'} className="h-12 text-base bg-black/30 border-white/10 flex-1 rounded-2xl" data-testid={`load-input-${acc.id}`} />
                        <Button onClick={() => handleLoad(acc.id, sg.id)} disabled={processing[`load_${acc.id}`]} className="h-12 px-7 rounded-full font-bold text-base" style={{ background: '#6C5CE7' }} data-testid={`load-btn-${acc.id}`}>
                          {processing[`load_${acc.id}`] ? <div className="spinner w-4 h-4" /> : 'Load'}
                        </Button>
                      </div>
                      {/* Source hint: explain where the money goes so the
                          player never wonders "why did my FP drop?". */}
                      <p className="text-[11px] text-gray-500 text-center" data-testid={`load-hint-${acc.id}`}>
                        Loading from{' '}
                        <span className="font-semibold text-gray-300">
                          {(fundSource[acc.id] || 'wallet') === 'fp' ? 'Free Play Credits' : 'Wallet $'}
                        </span>{' '}· 1{(fundSource[acc.id] || 'wallet') === 'fp' ? ' FP' : ' $'} = 1 in {sg.name}
                      </p>
                    </div>
                  )}

                  {/* Iter 142.3: Redeem modal removed — clicking
                      the Redeem button now triggers a one-click full
                      redeem. Provider rules (min/max multiplier, fee)
                      decide the actual payout, and funds route back to
                      the bucket the player loaded with (FP or cash).
                  */}

                  {/* Iter 142.10: Cashout Rules — admin-edited content
                      surfaced in a friendly big-font popup. Always visible
                      above Play Now so players can re-read the rules
                      anytime. */}
                  <button
                    onClick={() => setShowCashoutRules(sg)}
                    className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl text-sm font-bold transition-all hover:scale-[1.02]"
                    style={{
                      background: 'rgba(212,175,55,0.10)',
                      color: '#F5D76E',
                      border: '1.5px solid rgba(212,175,55,0.35)',
                    }}
                    data-testid={`cashout-rules-btn-${acc.id}`}
                  >
                    <ScrollText className="w-4 h-4" />
                    Cashout Rules
                  </button>

                  {/* Play Now */}
                  {sg.download_url && (
                    <a href={sg.download_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl text-base font-bold glow-d transition-all hover:scale-[1.02]"
                      style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D76E)', color: '#000', border: '2px solid rgba(212,175,55,0.5)' }} data-testid={`play-${acc.id}`}>
                      <Gamepad2 className="w-5 h-5" /> Play Now
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
        return host ? createPortal(popup, host) : popup;
      })()}

      {/* Iter 142.10: Cashout Rules popup — portal-mounted */}
      {showCashoutRules && (
        <CashoutRulesPopup
          gameId={showCashoutRules.id}
          onClose={() => setShowCashoutRules(null)}
        />
      )}

      {/* Iter 142.10: pre-load existing-balance gate */}
      {existingBalanceGate && (
        <ExistingBalanceWarning
          game={existingBalanceGate.sg}
          balance={existingBalanceGate.balance}
          processing={!!processing[`gate_redeem_${existingBalanceGate.acc.id}`]}
          onRedeem={handleGateRedeem}
          onCancel={() => setExistingBalanceGate(null)}
        />
      )}

      {insufficientLoadModal && (
        <div
          className="fixed inset-0 z-[160] flex items-end sm:items-center justify-center px-4 py-5 bg-black/70 backdrop-blur-sm"
          onClick={() => setInsufficientLoadModal(null)}
          data-testid="insufficient-balance-modal"
        >
          <div
            className="w-full max-w-sm rounded-[26px] overflow-hidden text-white"
            style={{
              background: 'linear-gradient(180deg, rgba(24,18,38,0.98), rgba(10,13,20,0.98))',
              border: '1px solid rgba(245,215,110,0.22)',
              boxShadow: '0 18px 44px rgba(0,0,0,0.45), 0 0 26px rgba(212,175,55,0.10)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-4 text-center">
              <div
                className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full"
                style={{ background: 'rgba(245,215,110,0.12)', border: '1px solid rgba(245,215,110,0.28)' }}
              >
                <AlertTriangle className="h-7 w-7 text-[#F5D76E]" />
              </div>
              <h3 className="text-xl font-black">Not Enough Balance</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-300">
                Please deposit funds before loading this game.
              </p>
              <p className="mt-2 text-xs leading-relaxed text-gray-500">
                {insufficientLoadModal.message}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 px-4 pb-4">
              <button
                type="button"
                onClick={() => setInsufficientLoadModal(null)}
                className="rounded-2xl py-3 text-sm font-bold text-gray-300 transition active:scale-[0.98]"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setInsufficientLoadModal(null);
                  setShowLoadModal(null);
                  setSelectedGame(null);
                  onDeposit?.();
                }}
                className="rounded-2xl py-3 text-sm font-black text-black transition active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D76E)' }}
                data-testid="insufficient-balance-deposit"
              >
                Deposit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
